import React from "react";
import moment from "moment";
import { getCompanyConstants } from "constants/CompanyConstants";
import { useTranslation } from "react-i18next";
import { DATE_FORMAT_DD_MM_YYYY_HH_mm } from "constants/DateConstant";
import { DELIVERY_OPTIONS } from "constants/DeliveryConstants";
import { sortPackingSlipLines } from "../../../functions/pickRoute";
import { expandOrderToPickLines } from "../../../functions/pickLines";
import { orderRefOf, summariseWaveBoxes } from "../../../functions/waveDocument";
import Barcode from "../Barcode";

/**
 * The rows of one slip.
 *
 * In a pick round the rows ARE the wave document's lines - the exact list, vakken and order the
 * scanner walks - so the paper in the box and the screen on the arm can never disagree. Outside a
 * round the order is expanded with the same function the round uses (functions/pickLines: package
 * contents from the product document, merged on article code) and sorted along the walking route.
 *
 * An order that cannot be expanded (a deleted product, a broken package) no longer crashes the
 * whole day's print: its slip says so and lists the order lines as they were bought.
 */
const slipRows = (order, { productIndex, pickRouteIndex, waveOrder }) => {
  if (waveOrder) {
    return {
      ok: true,
      rows: waveOrder.lines.map((line) => ({
        key: line.articleCode,
        name: line.label,
        variantLabel: null,
        weightText: line.sub,
        quantity: line.quantity,
        location: line.location,
        offRoute: line.offRoute,
        pickNote: line.pickNote,
      })),
    };
  }
  const expansion = expandOrderToPickLines(order, productIndex);
  if (!expansion.ok) {
    return {
      ok: false,
      problems: expansion.problems,
      rows: (order.products || [])
        .filter((line) => !line.giftCard)
        .map((line, index) => ({
          key: `${line.productId || "?"}-${index}`,
          name: line.name,
          variantLabel: line.type || null,
          weightText: line.variation && line.variation.weight ? `${line.variation.weight} gram` : null,
          quantity: line.quantity,
          location: null,
          offRoute: false,
          pickNote: null,
        })),
    };
  }
  return {
    ok: true,
    rows: sortPackingSlipLines(expansion.lines, pickRouteIndex).map((line) => ({
      key: line.articleCode,
      name: line.name,
      variantLabel: line.variantLabel,
      weightText: line.weight ? `${line.weight} gram` : null,
      quantity: line.quantity,
      location: null,
      offRoute: line.offRoute,
      pickNote: line.pickNote,
    })),
  };
};

/**
 * The first page of a round's slips: what to take into the freezer. A whole round is picked in one
 * go, straight into the boxes (planning/magazijn-inrichten.md), so the boxes come first, then the
 * orders in the order of the stack.
 */
const RoundCover = ({ wave }) => {
  const { t } = useTranslation();
  const boxes = summariseWaveBoxes(wave);
  const orders = [...(wave.orders || [])].sort((a, b) => a.sequence - b.sequence);
  return (
    <div style={styles.container} className="pagebreak round-cover" data-round-cover={wave.waveNumber}>
      <span style={styles.coverTitle}>{t("orderpicking.packing_slip.cover_title", { number: wave.waveNumber })}</span>
      <span style={styles.coverMeta}>
        {t("orderpicking.packing_slip.cover_meta", { day: wave.pickDay, orders: orders.length, lines: wave.lineCount || 0 })}
      </span>
      <div style={styles.coverTake}>
        <span style={styles.coverTakeLabel}>{t("orderpicking.packing_slip.cover_take")}</span>
        {boxes.small > 0 && <span style={styles.coverBoxes}>{t("orderpicking.packing_slip.cover_small", { count: boxes.small })}</span>}
        {boxes.large > 0 && <span style={styles.coverBoxes}>{t("orderpicking.packing_slip.cover_large", { count: boxes.large })}</span>}
        {boxes.unknown > 0 && <span style={styles.coverBoxes}>{t("orderpicking.packing_slip.cover_unknown", { count: boxes.unknown })}</span>}
        {boxes.uncertainOrders > 0 && <span style={styles.coverNote}>{t("orderpicking.packing_slip.cover_uncertain", { count: boxes.uncertainOrders })}</span>}
      </div>
      <table style={styles.coverTable}>
        <tbody>
          {orders.map((order) => (
            <tr key={order.orderRef}>
              <td style={styles.coverCell}>{order.sequence}.</td>
              <td style={styles.coverCell}>{order.orderId}</td>
              <td style={{ ...styles.coverCell, fontWeight: 800 }}>{order.boxAdvice || "-"}</td>
              <td style={styles.coverCell}>{order.deliveryType === DELIVERY_OPTIONS.PICKUP ? t("orderpicking.packing_slip.pickup") : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/**
 * The packing slip goes into the customer's box, so by default it carries only what the customer
 * should see (Sam, 2026-10-21): no vak, box advice, round number or pick note - the scanner shows
 * those. The order barcode stays: the picker scans it to start the order. `forPicking` prints the
 * picker's version for when the scanners are down: vak per line, box advice, "Ronde 3 · bestelling
 * 4 van 20" and the pick notes.
 *
 * @param {Object[]} data - orders (from useOrders or fetchWaveOrders)
 * @param {Map} productIndex - functions/pickLines.buildProductIndex over both shops' products
 * @param {Object} pickRouteIndex - functions/pickRoute.buildPickRouteIndex
 * @param {Object} waveOrdersByRef - `{ [orderRef]: waveOrder }` when printing a pick round
 * @param {Object} wave - the round itself, for its cover page
 * @param {boolean} forPicking - the picker's version, to pick from paper
 */
const PackingSlip = React.forwardRef((props, ref) => {
  const { t } = useTranslation();
  const { productIndex, pickRouteIndex, waveOrdersByRef, wave, forPicking = false } = props;
  const data = props.data;
  const filteredData = data.filter((order) => !order.containsOnlyGiftCards);

  const sortedData = filteredData.map((order) => {
    const waveOrder = waveOrdersByRef ? waveOrdersByRef[orderRefOf(order)] || null : null;
    return { ...order, slip: slipRows(order, { productIndex, pickRouteIndex, waveOrder }), waveOrder };
  });

  return (
    <div ref={ref}>
      {!!wave && <RoundCover wave={wave} />}
      {sortedData.map((order, index) => {
        const { shopSource } = order;
        const companyConstants = getCompanyConstants(shopSource);
        const address = order.customer.address.delivery;
        // The vak per line and the box: only on the picker's version of a round's slip
        const pickerRound = forPicking && !!order.waveOrder;
        return (
          <div key={index} style={styles.container} break className="pagebreak">
            <span style={styles.pageNumber}>{index}</span>
            <div style={styles.topRow}>
              <div style={styles.leftSection}>
                <div style={styles.logoContainer}>
                  <div>
                    <img src={companyConstants.LOGO_SRC} style={styles.logo} alt="logo" />
                  </div>
                </div>
                <span style={styles.pageTitle}>{t("packing_slip").toUpperCase()}</span>
                <span style={styles.clientInfoText}>
                  {address.firstName} {address.lastName}
                </span>
                <span style={styles.clientInfoText}>{address.companyName}</span>
                <span style={styles.clientInfoText}>
                  {address.street} {address.houseNumber}
                  {address.addition}
                </span>
                <span style={styles.clientInfoText}>
                  {address.zipCode}, {address.city}
                </span>
                <span style={styles.clientInfoText}>{order.customer.email}</span>
                <span style={styles.clientInfoText}>{order.customer.phoneNumber}</span>
                <span
                  style={{
                    display: "block",
                    fontWeight: "bold",
                    marginTop: 24,
                    color: TITLE_COLOR,
                    fontSize: TEXT_SIZE,
                  }}
                >
                  #{order.orderId}
                </span>
                {!!order.waveOrder && (
                  <div style={styles.waveBlock}>
                    <Barcode value={order.waveOrder.orderBarcode} moduleMm={0.33} heightMm={12} />
                    {!!wave && forPicking && (
                      <span style={styles.waveInfo}>
                        {t("orderpicking.packing_slip.round_of", {
                          number: wave.waveNumber,
                          sequence: order.waveOrder.sequence,
                          count: wave.orderCount,
                        })}
                      </span>
                    )}
                  </div>
                )}
                <span style={styles.clientInfoText}>
                  {t("order_date")}: {moment.unix(order.createdAt).format(DATE_FORMAT_DD_MM_YYYY_HH_mm)}
                </span>
                <span style={styles.clientInfoText}>
                  {order.delivery.type === DELIVERY_OPTIONS.SEND ? t("shipping_date") : t("pickup_date")}:{" "}
                  {moment(order.delivery.date, "DD-MM-YYYY").format("dddd D MMMM YYYY")}
                </span>
                <span style={styles.clientInfoText}>
                  {t("order_notes")}: {!!order.orderNotes ? order.orderNotes : "-"}
                </span>
              </div>
              <div style={styles.rightSection}>
                <span style={styles.senderCompanyName}>{companyConstants.COMPANY_NAME}</span>
                <div style={{ display: "block" }}>
                  <span style={styles.senderInfoText}>{companyConstants.STREET_AND_NUMBER}</span>
                  <span style={styles.senderInfoText}>{companyConstants.ZIP_AND_CITY}</span>
                  <span style={styles.senderInfoText}>{companyConstants.COUNTRY}</span>
                </div>
                <div style={{ display: "block" }}>
                  <span style={styles.senderInfoText}>
                    {t("kvk_number")}: {companyConstants.KVK}
                  </span>
                  <span style={styles.senderInfoText}>
                    {t("vat_number")}: {companyConstants.VAT}
                  </span>
                </div>
              </div>
            </div>
            <div style={styles.bottomRow}>
              {pickerRound && (
                <div style={styles.boxBanner}>
                  <span style={styles.boxAdvice}>{order.waveOrder.boxAdvice}</span>
                  {order.waveOrder.deliveryType === DELIVERY_OPTIONS.PICKUP && (
                    <span style={styles.pickupBadge}>{t("orderpicking.packing_slip.pickup")}</span>
                  )}
                  {order.waveOrder.boxes.length > 1 && (
                    <div style={styles.boxContents}>
                      {order.waveOrder.boxes.map((box) => (
                        <div key={box.n}>
                          {t("orderpicking.packing_slip.box_n", { n: box.n, count: order.waveOrder.boxes.length })}: {box.contents}
                        </div>
                      ))}
                    </div>
                  )}
                  {order.waveOrder.linesWithoutWeight > 0 && (
                    <div style={styles.boxContents}>{t("orderpicking.packing_slip.box_unreliable")}</div>
                  )}
                </div>
              )}
              {!order.slip.ok && (
                <div style={styles.mappingFailed}>
                  <strong>{t("orderpicking.packing_slip.mapping_failed")}</strong>
                  {order.slip.problems.map((problem) => (
                    <div key={problem}>{problem}</div>
                  ))}
                </div>
              )}
              <div style={styles.divider} />
              <div style={styles.clientInfoRow}>
                <div style={styles.table}>
                  <div style={styles.tableRow}>
                    {pickerRound && (
                      <div style={styles.tableColLocation}>
                        <span style={styles.tableCellHeader}>{t("orderpicking.packing_slip.vak")}</span>
                      </div>
                    )}
                    <div style={styles.tableColName}>
                      <span style={styles.tableCellHeader}>{t("name")}</span>
                    </div>
                    <div style={styles.tableColUnitPrice}>
                      <span style={styles.tableCellHeader}>{t("weight")}</span>
                    </div>
                    <div style={styles.tableColQty}>
                      <span style={styles.tableCellHeader}>{t("quantity")}</span>
                    </div>
                    <div style={styles.tableColPicked}>
                      <span style={styles.tableCellHeader}>{t("picked")}</span>
                    </div>
                  </div>
                  {order.slip.rows.map((row) => {
                    const baseName = row.variantLabel ? `${row.name} (${row.variantLabel})` : row.name;
                    // Not on the pick route: it sits after the route at the bottom of the slip, so
                    // mark it - otherwise the picker silently walks past an item that has no spot
                    const name = row.offRoute ? "* " + baseName : baseName;
                    return (
                      <div key={row.key}>
                        <div style={styles.tableDivider} />
                        <div style={styles.tableRow}>
                          {pickerRound && (
                            <div style={styles.tableColLocation}>
                              <span style={{ ...styles.tableCell, fontWeight: 700, color: "#000" }}>{row.location || "-"}</span>
                            </div>
                          )}
                          <div style={styles.tableColName}>
                            <span style={styles.tableCell}>{name}</span>
                            {forPicking && !!row.pickNote && <div style={styles.pickNote}>{row.pickNote}</div>}
                          </div>
                          <div style={styles.tableColUnitPrice}>
                            <span style={styles.tableCell}>
                              <span style={styles.tableCell}>{row.weightText || "-"}</span>
                            </span>
                          </div>
                          <div style={styles.tableColQty}>
                            <span style={styles.tableCell}>{row.quantity}</span>
                          </div>
                          <div style={styles.tableColPicked}>
                            <div style={styles.checkbox} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
});

const TITLE_SIZE = 18;
const TITLE_MARGIN = 10;
const TEXT_SIZE = 14;
const TITLE_COLOR = "#283147";
const TEXT_COLOR = "#6b6b6b";

const styles = {
  container: {
    display: "block",
    position: "relative",
    flex: 1,
    padding: 30,
    color: "#000",
    WebkitPrintColorAdjust: "exact",
    printColorAdjust: "exact",
    mixBlendMode: "normal",
    // Neither font is loaded by the app; without a generic fallback the browser picked a serif
    fontFamily: "Montserrat, 'Open Sans', Arial, Helvetica, sans-serif",
  },
  contentContainer: {
    display: "block",
    justifyContent: "center",
    alignItems: "center",
  },
  logoContainer: {
    display: "block",
    marginBottom: 10,
  },
  logo: {
    display: "block",
    maxWidth: 150,
    maxHeight: 250,
    aspectRatio: "contain",
  },
  topRow: {
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row",
    flex: 1,
    marginRight: -15,
    marginLeft: -15,
    paddingTop: 12,
    paddingBottom: 12,
  },
  bottomRow: {
    flex: 3,
  },
  pageTitle: {
    display: "block",
    fontWeight: "bold",
    color: TITLE_COLOR,
    marginTop: TITLE_MARGIN,
    marginBottom: TITLE_MARGIN,
    fontSize: TITLE_SIZE,
  },
  leftSection: {
    position: "relative",
    width: "100%",
    flex: 1,
    maxWidth: "60%",
    paddingRight: 15,
    paddingLeft: 15,
  },
  rightSection: {
    position: "relative",
    width: "100%",
    flex: 1,
    maxWidth: "40%",
    paddingRight: 15,
    paddingLeft: 15,
    textAlign: "right",
  },
  senderCompanyName: {
    display: "block",
    fontWeight: 800,
    color: TITLE_COLOR,
    marginBottom: TITLE_MARGIN,
    fontSize: TITLE_SIZE,
  },
  senderInfoText: {
    display: "block",
    marginBottom: 4,
    color: TEXT_COLOR,
    fontSize: TEXT_SIZE,
    width: "100%",
  },
  clientInfoText: {
    display: "block",
    marginBottom: 2,
    fontSize: TEXT_SIZE,
    color: TEXT_COLOR,
  },
  right: {
    justifyContent: "flex-end",
  },
  clientInfoRow: {
    paddingTop: 12,
    paddingBottom: 12,
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row",
    flex: 1,
    width: "100%",
  },
  divider: {
    display: "flex",
    borderTopWidth: 1,
    borderTopStyle: "solid",
    borderTopColor: "#c5c5c5",
  },
  tableDivider: {
    display: "flex",
    borderTopWidth: 1,
    borderTopStyle: "solid",
    borderTopColor: "#e3e3e3",
  },
  table: {
    display: "table",
    flex: 1,
    width: "100%",
  },
  tableRow: {
    display: "flex",
    margin: "auto",
    flexDirection: "row",
    flex: 1,
    marginTop: 8,
    width: "100%",
  },
  subItemTableRow: {
    display: "flex",
    margin: "auto",
    flexDirection: "row",
    flex: 1,
    marginTop: 0,
    width: "100%",
    fontWeight: 400,
  },
  tableColName: {
    flex: 6,
    textAlign: "left",
  },
  subItemsList: {
    marginLeft: 10,
    marginTop: 2,
  },
  subItem: {
    fontSize: TEXT_SIZE - 2,
    color: TEXT_COLOR,
    lineHeight: "18px",
  },

  tableColUnitPrice: {
    flex: 2,
    textAlign: "right",
  },
  tableColQty: {
    flex: 2,
    textAlign: "right",
  },
  tableColPicked: {
    flex: 1,
    textAlign: "center",
  },
  tableCol: {
    flex: 1,
    width: "25%",
  },
  tableCellHeader: {
    margin: 5,
    fontSize: TEXT_SIZE + 1,
    fontWeight: 800,
    color: TITLE_COLOR,
  },
  tableCell: {
    margin: 5,
    fontSize: TEXT_SIZE,
    color: TEXT_COLOR,
  },
  pageNumber: {
    position: "absolute",
    left: 30,
    fontSize: 15,
    color: "#a1a1a1",
    display: "block",
  },
  waveBlock: {
    display: "block",
    marginTop: 6,
    marginBottom: 6,
  },
  waveInfo: {
    display: "block",
    marginTop: 2,
    fontSize: TEXT_SIZE - 1,
    color: TITLE_COLOR,
    fontWeight: 600,
  },
  coverTitle: {
    display: "block",
    fontSize: 40,
    fontWeight: 800,
    color: "#000",
  },
  coverMeta: {
    display: "block",
    fontSize: TEXT_SIZE + 2,
    color: TITLE_COLOR,
    marginBottom: 24,
  },
  coverTake: {
    display: "block",
    border: "3px solid #000",
    padding: "12px 16px",
    marginBottom: 24,
  },
  coverTakeLabel: {
    display: "block",
    fontSize: 16,
    fontWeight: 700,
    letterSpacing: 2,
    marginBottom: 6,
  },
  coverBoxes: {
    display: "block",
    fontSize: 34,
    fontWeight: 800,
    lineHeight: 1.25,
  },
  coverNote: {
    display: "block",
    fontSize: TEXT_SIZE,
    marginTop: 8,
  },
  coverTable: {
    borderCollapse: "collapse",
    fontSize: TEXT_SIZE + 1,
  },
  coverCell: {
    padding: "4px 16px 4px 0",
    borderBottom: "1px solid #ccc",
  },
  boxBanner: {
    display: "block",
    border: "2px solid #000",
    padding: "6px 10px",
    marginBottom: 10,
  },
  boxAdvice: {
    fontSize: 26,
    fontWeight: 800,
    letterSpacing: 1,
    color: "#000",
  },
  pickupBadge: {
    marginLeft: 16,
    padding: "2px 8px",
    border: "2px solid #000",
    fontSize: 18,
    fontWeight: 800,
    verticalAlign: "middle",
  },
  boxContents: {
    fontSize: TEXT_SIZE - 2,
    color: "#000",
    marginTop: 4,
  },
  mappingFailed: {
    display: "block",
    border: "2px solid #cf1322",
    color: "#cf1322",
    padding: "6px 10px",
    marginBottom: 10,
    fontSize: TEXT_SIZE - 1,
  },
  tableColLocation: {
    flex: 1,
    textAlign: "left",
  },
  pickNote: {
    marginLeft: 5,
    fontSize: TEXT_SIZE - 2,
    color: "#d46b08",
    fontWeight: 600,
  },
  checkbox: {
    width: 28,
    height: 28,
    border: "1px solid #000",
    display: "inline-block",
    alignSelf: "center",
  },
};

export default PackingSlip;
